import { create } from 'zustand';
import { ColumnState } from 'ag-grid-community';
import { GridRowData } from './Dto_PRD3040_R01';
import { DateRange } from 'react-day-picker';
import { getCurrentMonthDateRange } from 'src/utils/date/dateUtils';
import { PRD3040_R01SubtotalRow } from './utils/subtotalUtils';

// 탭 타입 정의 및 UI 상태 타입 (생산실적)
export type PRD3040_R01TabType = 'date' | 'item' | 'workplace';

export interface PRD3040_R01TabConfig {
    id: PRD3040_R01TabType;
    label: string;
    description: string;
}

export type PRD3040_R01DateStockData = any;
export type PRD3040_R01ItemStockData = any;
export type PRD3040_R01WorkplaceStockData = any;   

export interface PRD3040_R01TabData {
    date: PRD3040_R01DateStockData[];
    item: PRD3040_R01ItemStockData[];
    workplace: PRD3040_R01WorkplaceStockData[];
}

// 탭별 그리드 데이터 (소계 포함)
export interface PRD3040_R01TabGridData {
    date: GridRowData[];
    item: GridRowData[];
    workplace: GridRowData[];
}

// 탭별 소계 데이터
export interface PRD3040_R01TabSubtotalData {
    date: PRD3040_R01SubtotalRow[];
    item: PRD3040_R01SubtotalRow[];
    workplace: PRD3040_R01SubtotalRow[];
}

export interface PRD3040_R01TabColumnStates {
    date: ColumnState[];
    item: ColumnState[];
    workplace: ColumnState[];
}

export interface PRD3040_R01TabLoadingStates {
    date: boolean;
    item: boolean;
    workplace: boolean;
}

// 탭별 선택된 행 ID 목록
export interface PRD3040_R01TabSelectedIds {
    date: string[];
    item: string[];
    workplace: string[];
}

export interface PRD3040_R01TabScrollTop {
    date: number | null;
    item: number | null;
    workplace: number | null;
}

const defaultPRD3040_R01TabScrollTop = (): PRD3040_R01TabScrollTop => ({
    date: null,
    item: null,
    workplace: null,
});

// 필터 인터페이스 (API에서 사용)
export interface PRD3040_R01Filter {
    logicCenter: string;
    warehouseCd: string;
    itemCd: string;
    itemNm: string;
    brandCd: string;
    productDateRange: DateRange | undefined;
    compCd : string;
    workplaceCd : string;
    productEmp : string;
    productEmpNm: string;
    startDate : string;
    endDate : string;

}

interface PRD3040_R01Store {
    // 기본 필터
    PRD3040_R01Filter: PRD3040_R01Filter;
    setPRD3040_R01Filter: (filter: Partial<PRD3040_R01Filter>) => void;
    
    
    // 활성 탭
    PRD3040_R01activeTab: PRD3040_R01TabType;
    setPRD3040_R01ActiveTab: (tab: PRD3040_R01TabType) => void;
    
    // 탭별 데이터
    PRD3040_R01tabData: PRD3040_R01TabData;
    setPRD3040_R01TabData: (tabType: PRD3040_R01TabType, data: any[]) => void;
    
    // 탭별 그리드 데이터 (소계 포함)
    PRD3040_R01tabGridData: PRD3040_R01TabGridData;
    setPRD3040_R01TabGridData: (tabType: PRD3040_R01TabType, data: GridRowData[]) => void;
    
    // 탭별 소계 데이터
    PRD3040_R01tabSubtotalData: PRD3040_R01TabSubtotalData;
    setPRD3040_R01TabSubtotalData: (tabType: PRD3040_R01TabType, data: PRD3040_R01SubtotalRow[]) => void;
    
    // 소계 표시 여부
    PRD3040_R01showSubtotals: boolean;
    setPRD3040_R01ShowSubtotals: (show: boolean) => void;
    
    // 탭별 컬럼 상태
    PRD3040_R01tabColumnStates: PRD3040_R01TabColumnStates;
    setPRD3040_R01TabColumnState: (tabType: PRD3040_R01TabType, columnState: any[]) => void;
    
    // 탭별 로딩 상태
    PRD3040_R01tabLoadingStates: PRD3040_R01TabLoadingStates;
    setPRD3040_R01TabLoadingState: (tabType: PRD3040_R01TabType, loading: boolean) => void;
    
    // 탭별 선택 상태
    PRD3040_R01tabSelectedIds: PRD3040_R01TabSelectedIds;
    setPRD3040_R01TabSelectedIds: (tabType: PRD3040_R01TabType, ids: string[]) => void;
    PRD3040_R01toggleSelectedId: (tabType: PRD3040_R01TabType, id: string) => void;
    PRD3040_R01clearTabSelectedIds: (tabType: PRD3040_R01TabType) => void;

    PRD3040_R01tabScrollTop: PRD3040_R01TabScrollTop;
    setPRD3040_R01TabScrollTop: (tabType: PRD3040_R01TabType, top: number | null) => void;
    resetAllPRD3040_R01TabScrollTop: () => void;
    
    // 초기화
    initData: () => void;
}


const defaultDateRange: DateRange = getCurrentMonthDateRange();

export const defaultPRD3040_R01Filter: PRD3040_R01Filter = {
    logicCenter: '',
    warehouseCd: '',
    itemCd: '',
    itemNm: '',
    brandCd: '',
    productDateRange: defaultDateRange,
	compCd : '',
    workplaceCd : '',
    productEmp : '',
    productEmpNm: '',
    startDate : '',
    endDate : ''
};

export const usePRD3040_R01Store = create<PRD3040_R01Store>((set, get) => ({
    // 기본 필터
    PRD3040_R01Filter: {
        logicCenter: '',
        warehouseCd: '',
        itemCd: '',
        itemNm: '',
        brandCd: '',
        productDateRange: defaultDateRange,
        compCd : '',
        workplaceCd : '',
        productEmp : '',
        productEmpNm: '',
        startDate : '',
        endDate : ''
    },
    setPRD3040_R01Filter: (filter) => set((state) => ({
        PRD3040_R01Filter: { ...state.PRD3040_R01Filter, ...filter }
    })),
    
    // 활성 탭
    PRD3040_R01activeTab: 'date',
    setPRD3040_R01ActiveTab: (tab) => {
        set({ PRD3040_R01activeTab: tab });
    },
    
    // 탭별 데이터
    PRD3040_R01tabData: {
        date: [],
        item: [],
        workplace: [],
    },
    setPRD3040_R01TabData: (tabType, data) => set((state) => ({
        PRD3040_R01tabData: {
            ...state.PRD3040_R01tabData,
            [tabType]: data
        }
    })),
    
    // 탭별 그리드 데이터 (소계 포함)
    PRD3040_R01tabGridData: {
        date: [],
        item: [],
        workplace: [],
    },
    setPRD3040_R01TabGridData: (tabType, data) => set((state) => ({
        PRD3040_R01tabGridData: {
            ...state.PRD3040_R01tabGridData,
            [tabType]: data
        }
    })),
    
    // 탭별 소계 데이터
    PRD3040_R01tabSubtotalData: {
        date: [],
        item: [],
        workplace: [],
    },
    setPRD3040_R01TabSubtotalData: (tabType, data) => set((state) => ({
        PRD3040_R01tabSubtotalData: {
            ...state.PRD3040_R01tabSubtotalData,
            [tabType]: data
        }
    })),
    
    // 소계 표시 여부
    PRD3040_R01showSubtotals: false,
    setPRD3040_R01ShowSubtotals: (show) => set({ PRD3040_R01showSubtotals: show }),
    
    // 탭별 컬럼 상태
    PRD3040_R01tabColumnStates: {
        date: [],
        item: [],
        workplace: [],
    },
    setPRD3040_R01TabColumnState: (tabType, columnState) => set((state) => ({
        PRD3040_R01tabColumnStates: {
            ...state.PRD3040_R01tabColumnStates,
            [tabType]: columnState
        }
    })),
    
    // 탭별 로딩 상태
    PRD3040_R01tabLoadingStates: {
        date: false,
        item: false,
        workplace: false,
    },
    setPRD3040_R01TabLoadingState: (tabType, loading) => set((state) => ({
        PRD3040_R01tabLoadingStates: {
            ...state.PRD3040_R01tabLoadingStates,
            [tabType]: loading
        }
    })),
    
    // 탭별 선택 상태
    PRD3040_R01tabSelectedIds: {
        date: [],
        item: [],
        workplace: [],
    },
    setPRD3040_R01TabSelectedIds: (tabType, ids) => set((state) => ({
        PRD3040_R01tabSelectedIds: {
            ...state.PRD3040_R01tabSelectedIds,
            [tabType]: ids
        }
    })),
    PRD3040_R01toggleSelectedId: (tabType, id) => set((state) => {
        const current = state.PRD3040_R01tabSelectedIds[tabType] || [];
        const exists = current.includes(id);
        const next = exists ? current.filter(v => v !== id) : [...current, id];
        return {
            PRD3040_R01tabSelectedIds: {
                ...state.PRD3040_R01tabSelectedIds,
                [tabType]: next
            }
        } as any;
    }),
    PRD3040_R01clearTabSelectedIds: (tabType) => set((state) => ({
        PRD3040_R01tabSelectedIds: {
            ...state.PRD3040_R01tabSelectedIds,
            [tabType]: []
        }
    })),

    PRD3040_R01tabScrollTop: defaultPRD3040_R01TabScrollTop(),
    setPRD3040_R01TabScrollTop: (tabType, top) => set((state) => ({
        PRD3040_R01tabScrollTop: {
            ...state.PRD3040_R01tabScrollTop,
            [tabType]: top,
        },
    })),
    resetAllPRD3040_R01TabScrollTop: () => set({ PRD3040_R01tabScrollTop: defaultPRD3040_R01TabScrollTop() }),
    
    // 초기화
    initData: () => set({
        PRD3040_R01Filter: {
            logicCenter: '',
            warehouseCd: '',
            productEmpNm: '',
            itemCd: '',
            itemNm: '',
            brandCd: '',
            productDateRange: defaultDateRange,
            compCd : '',
            workplaceCd : '',
            productEmp : '',
            startDate : '',
            endDate : ''
        },
        PRD3040_R01activeTab: 'date',
        PRD3040_R01tabData: {
            date: [],
            item: [],
            workplace: [],
        },
        PRD3040_R01tabGridData: {
            date: [],
            item: [],
            workplace: [],
        },
        PRD3040_R01tabSubtotalData: {
            date: [],
            item: [],
            workplace: [],
        },
        PRD3040_R01showSubtotals: false,
        PRD3040_R01tabColumnStates: {
            date: [],
            item: [],
            workplace: [],
        },
        PRD3040_R01tabLoadingStates: {
            date: false,
            item: false,
            workplace: false,
        },
        PRD3040_R01tabSelectedIds: {
            date: [],
            item: [],
            workplace: [],
        },
        PRD3040_R01tabScrollTop: defaultPRD3040_R01TabScrollTop(),
    }),
}));

export default usePRD3040_R01Store;
